// Wizard transport. Mirrors the pattern the Favorites Designer already uses in
// this admin (shared/api.js): CSRF from the meta tag, same-origin cookies.
//
// Every write hits the same PATCH .../wizard endpoint the ERB form posted to, so
// it runs the identical apply_* method on the server. This file adds a transport,
// not a second way to save.

const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.content || '';

const parse = async (response) => {
  const body = await response.json().catch(() => ({}));
  if (!response.ok && response.status !== 422) {
    throw new Error(body.error || `Request failed (${response.status})`);
  }
  return body;
};

export const wizardPath = (productId) => `/admin/shop/products/${productId}/wizard`;

// Full payload for a step. `previewSchoolId` only changes whose eyes the preview
// renders through; it never changes what is saved.
export const fetchPayload = async (productId, step, previewSchoolId) => {
  const params = new URLSearchParams({ step });
  if (previewSchoolId) params.set('preview_school_id', previewSchoolId);
  const response = await fetch(`${wizardPath(productId)}.json?${params}`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  return parse(response);
};

// Save one step. `fields` is whatever that step's server-side applier reads --
// deliberately the same parameter names the ERB forms posted.
export const saveStep = async (productId, step, fields, previewSchoolId) => {
  const params = new URLSearchParams({ step });
  if (previewSchoolId) params.set('preview_school_id', previewSchoolId);

  const response = await fetch(`${wizardPath(productId)}.json?${params}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-CSRF-Token': csrfToken(),
    },
    credentials: 'same-origin',
    body: JSON.stringify({ ...fields, stay: '1' }),
  });
  return parse(response);
};

// Packages are their own records, so they use the variants controller the ERB
// step used. Returns nothing useful; the caller refetches the payload.
export const createVariant = async (productId, attrs) => {
  const response = await fetch(`/admin/shop/products/${productId}/variants`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-CSRF-Token': csrfToken(),
    },
    credentials: 'same-origin',
    body: JSON.stringify({ shop_product_variant: attrs }),
  });
  return parse(response);
};

// The same endpoint the one-page editor used to post to -- ONE writer for what a
// package may be called, which legacy option it sells through and which badges it
// shows, rather than a second copy of those rules in the wizard (#5446).
// `extra` rides OUTSIDE the shop_product_variant wrapper -- metadata-backed settings
// (the per-package Printess design, the card image) are read from the top level by
// the controller, which writes them explicitly rather than through strong params.
export const updateVariant = async (productId, variantId, attrs, extra = {}) => {
  const response = await fetch(`/admin/shop/products/${productId}/variants/${variantId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-CSRF-Token': csrfToken(),
    },
    credentials: 'same-origin',
    body: JSON.stringify({ shop_product_variant: attrs, ...extra }),
  });
  return parse(response);
};

export const deleteVariant = async (productId, variantId) => {
  const response = await fetch(`/admin/shop/products/${productId}/variants/${variantId}`, {
    method: 'DELETE',
    headers: { Accept: 'application/json', 'X-CSRF-Token': csrfToken() },
    credentials: 'same-origin',
  });
  return parse(response);
};

// The order customers see the packages in. The whole order goes in one request --
// see Admin::Shop::VariantsController#reorder for why it is not a PATCH per row.
export const reorderVariants = async (productId, ids) => {
  const response = await fetch(`/admin/shop/products/${productId}/variants/reorder.json`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-CSRF-Token': csrfToken(),
    },
    credentials: 'same-origin',
    body: JSON.stringify({ ids }),
  });
  return parse(response);
};
